import { formatMoney, type Reason, type SelectableItem } from '@nvd/claim-shared';
import { Icon } from '../components/Icon';
import { QtyStepper } from '../components/QtyStepper';
import { StepLayout } from '../components/StepLayout';
import { usePortal } from '../Portal';

const chipClass = (active: boolean) => `nvd-claim-portal__reason${active ? ' nvd-claim-portal__reason--active' : ''}`;

/** Items and reasons in one step: picking a reason selects the item, so each one can be claimed for its own issue. */
export function OrderItems() {
  const { config, t, state, dispatch } = usePortal();
  const view = state.view;
  if (!view) return null;

  const reasons = config.reasons;
  const selected = state.draft.items;

  const setReason = (item: SelectableItem, reasonId: string) =>
    dispatch({ type: 'SET_ITEM_REASON', id: item.id, reasonId, quantity: item.quantity });

  /** Chips toggle: tapping the chosen issue again clears the item. */
  const toggle = (item: SelectableItem, reason: Reason) =>
    setReason(item, selected[item.id]?.reasonId === reason.id ? '' : reason.id);

  const applyToAll = (reasonId: string) => {
    for (const item of view.items) setReason(item, reasonId);
  };

  // Only reflects a reason in the bulk picker while every item still shares it.
  const first = view.items[0] ? selected[view.items[0].id]?.reasonId : undefined;
  const shared = first && view.items.every((item) => selected[item.id]?.reasonId === first) ? first : '';
  const units = Object.values(selected).reduce((sum, item) => sum + item.quantity, 0);
  // A free-text reason with nothing typed yet is a claim in progress, not a finished selection.
  const incomplete = Object.values(selected).some(
    (item) => !item.note.trim() && reasons.find((reason) => reason.id === item.reasonId)?.freeText
  );

  return (
    <StepLayout
      title={t('items.title')}
      onBack={() => dispatch({ type: 'RESET' })}
      backLabel={t('common.startOver')}
      onNext={() => dispatch({ type: 'GO', step: 'issue' })}
      nextDisabled={Object.keys(selected).length === 0 || incomplete}
      summary={units > 0 && (units === 1 ? t('items.countOne') : t('items.count', { count: units }))}
    >
      {view.items.length === 0 ? (
        <p className="nvd-claim-portal__step-text">{t('items.empty')}</p>
      ) : (
        <>
          {view.items.length > 1 && (
            <label className="nvd-claim-portal__apply-all">
              <span className="nvd-claim-portal__apply-all-label">{t('items.bulkLabel')}</span>
              <select className="nvd-claim-portal__select" value={shared} onChange={(event) => applyToAll(event.target.value)}>
                <option value="">{t('items.bulkPlaceholder')}</option>
                {reasons.map((reason) => (
                  <option key={reason.id} value={reason.id}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <ul className="nvd-claim-portal__items">
            {view.items.map((item) => {
              const draftItem = selected[item.id];
              const chosen = draftItem ? reasons.find((reason) => reason.id === draftItem.reasonId) : undefined;
              const groupLabel = `${t('items.title')} — ${item.title}`;
              return (
                <li key={item.id} className={`nvd-claim-portal__item${draftItem ? ' nvd-claim-portal__item--selected' : ''}`}>
                  <div className="nvd-claim-portal__item-row">
                    <div className="nvd-claim-portal__item-main">
                      {item.imageUrl ? (
                        <img className="nvd-claim-portal__item-image" src={item.imageUrl} alt="" loading="lazy" />
                      ) : (
                        <span className="nvd-claim-portal__item-image" aria-hidden="true" />
                      )}
                      <span className="nvd-claim-portal__item-info">
                        <span className="nvd-claim-portal__item-title">{item.title}</span>
                        {item.variantTitle && <span className="nvd-claim-portal__item-variant">{item.variantTitle}</span>}
                        <span className="nvd-claim-portal__item-price">
                          {formatMoney(item.unitPrice)} × {item.quantity}
                        </span>
                      </span>
                    </div>
                    {draftItem && (
                      <div className="nvd-claim-portal__item-actions">
                        {item.quantity > 1 && (
                          <QtyStepper
                            value={draftItem.quantity}
                            max={item.quantity}
                            onChange={(next) => dispatch({ type: 'SET_ITEM_QUANTITY', id: item.id, quantity: next })}
                          />
                        )}
                        <button type="button" className="nvd-claim-portal__link" onClick={() => setReason(item, '')}>
                          {t('items.clear')}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="nvd-claim-portal__reasons" role="group" aria-label={groupLabel}>
                    {reasons.map((reason) => {
                      const active = draftItem?.reasonId === reason.id;
                      return (
                        <button
                          key={reason.id}
                          type="button"
                          aria-pressed={active}
                          className={chipClass(active)}
                          onClick={() => toggle(item, reason)}
                        >
                          <Icon name={reason.icon} />
                          {reason.label}
                        </button>
                      );
                    })}
                  </div>

                  {chosen?.freeText && (
                    <label className="nvd-claim-portal__field">
                      <span className="nvd-claim-portal__label">{t('items.noteLabel')}</span>
                      <input
                        className="nvd-claim-portal__input nvd-claim-portal__input--compact"
                        autoFocus
                        maxLength={500}
                        value={draftItem?.note ?? ''}
                        onChange={(event) => dispatch({ type: 'SET_ITEM_NOTE', id: item.id, note: event.target.value })}
                      />
                    </label>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </StepLayout>
  );
}
